import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { Observable } from 'rxjs/internal/Observable';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { filter } from 'rxjs/operators';
import { CommonRouteSegmentEnum } from '../../models/common-route-segment.enum';
import { AppStateService } from '../app-state.service';
import { authSsoCodeFlowConfig } from './auth.config';

@Injectable({ providedIn: 'root' })
export class AuthService {
	private onAuthenticatedSubject: ReplaySubject<undefined> = new ReplaySubject<undefined>(1);
	get onAuthenticated$(): Observable<undefined> {
		return this.onAuthenticatedSubject;
	}
	private isOnAuthenticatedTriggered: boolean = false;
	private readonly authorizationUidKey = 'authorizationUid';
	constructor(
		private oauthService: OAuthService,
		private appStateService: AppStateService,
		private router: Router
	) {}

	public async initAuth() {
		authSsoCodeFlowConfig.customQueryParams = {
			default_domain: 'is',
			...(this.appStateService.getImpersonatedAs() != null && {
				impersonated_as: this.appStateService.getImpersonatedAs()
			})
		};
		this.oauthService.configure(authSsoCodeFlowConfig);
		this.oauthService.setupAutomaticSilentRefresh({}, 'access_token');

		this.oauthService.events.subscribe(({ type }: OAuthEvent) => {
			if (!this.isOnAuthenticatedTriggered && this.hasValidAccessToken()) {
				this.isOnAuthenticatedTriggered = true;
				this.onAuthenticatedSubject.next(undefined);
			}
			if (type == 'token_received' && this.hasValidAccessToken()) {
				this.ensureAuthSessionId();
				if (this.oauthService.state) {
					let state = this.oauthService.state;
					this.router.navigateByUrl(decodeURIComponent(state));
					this.oauthService.state = undefined;
				}

				this.appStateService.reloadUserInfo();
			}
		});
		if (this.oauthService.hasValidAccessToken()) {
			this.ensureAuthSessionId();
			await this.oauthService.loadDiscoveryDocument();
			this.appStateService.reloadUserInfo();
		} else if (this.isAnonymousAccessAllowed()) {
			await this.oauthService.loadDiscoveryDocument();
		} else {
			await this.oauthService.loadDiscoveryDocumentAndLogin({
				state: this.getCurrentUrl() // При редиректе на auth текущий урл будет передан в state, после возврата в приложение сможем извлечь его и перейти по изначальному урлу
			});
		}

		this.oauthService.events
			.pipe(filter((e) => e.type == 'token_refresh_error'))
			.subscribe(async (e) => {
				localStorage.clear();
				await this.oauthService.initCodeFlow(this.getCurrentUrl());
			});
	}

	public logout(): void {
		localStorage.clear();
		window.location.href = `${environment.authSsoUrl}/connect/logout?redirect_uri=${environment.oldLKKLink}`;
	}

	public hasValidAccessToken(): boolean {
		return this.oauthService.hasValidAccessToken();
	}

	private isAnonymousAccessAllowed(): boolean {
		return window.location.pathname.startsWith(`/${CommonRouteSegmentEnum.Alfa}`);
	}

	private getCurrentUrl(): string {
		return window.location.pathname + window.location.search;
	}

	public getAuthSessionId(): string | null {
		return localStorage.getItem(this.authorizationUidKey);
	}
	private ensureAuthSessionId(): void {
		if (!localStorage.getItem(this.authorizationUidKey)) {
			localStorage.setItem(this.authorizationUidKey, crypto.randomUUID());
		}
	}
}
